/**
 * IMPORST
 */

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CircleArrowRight, LogOut } from "lucide-react";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import Image from "@/common/assets/png/logo-let-scom.png";
import { abrirBackoffice } from "@/common/navigation/abrir-backoffice";
import {
  getMenusByFeatures,
  type SidebarMenuItem,
  splitMenusByType,
} from "@/common/navigation/menus-by-feature";

function Sidebar() {
  const { setUser, setIsAuthenticated, features, logout } = useStoreZustandUserAuth();

  const router = useNavigate();
  const location = useLocation();

  const [openSidbar, setOpenSidbar] = useState(false);

  const currentRoute = location.pathname;
  const { internalMenus, externalMenus } = splitMenusByType(getMenusByFeatures(features));

  const handleNavigation = (menu: SidebarMenuItem) => {
    if (menu.external) {
      if (menu.abrirComSessao) void abrirBackoffice(menu.link);
      else window.open(menu.link, "_blank", "noopener,noreferrer");
      return;
    }

    router(menu.link);
  };

  const handleLogout = () => {
    router("/");
    logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const renderMenuItem = (menu: SidebarMenuItem, showActiveState = true) => {
    const isActive = showActiveState
      ? menu.subRoutes
        ? menu.subRoutes.some((route) => currentRoute === route)
        : currentRoute === menu.link
      : false;

    return (
      <li
        key={menu.id}
        onClick={() => handleNavigation(menu)}
        className={`flex cursor-pointer items-center gap-x-4 p-3 text-sm text-gray-300 ${
          openSidbar ? "w-full" : "w-10"
        } ${isActive ? "bg-[#363740]" : ""} rounded-md hover:bg-light-white`}
      >
        <span className="float-left block text-2xl">{menu.icon}</span>
        <span className={`flex-1 text-base font-semibold ${!openSidbar && "hidden"}`}>
          {menu.title}
        </span>
      </li>
    );
  };

  return (
    <div
      className={`bg-gray-custom900 p-5 pt-8 ${openSidbar ? "w-72" : "w-20"} relative duration-500`}
    >
      <CircleArrowRight
        className={`absolute -right-2 top-9 size-6 cursor-pointer rounded-full border border-dark-purple bg-white text-blue-900 ${
          openSidbar ? "rotate-180" : "rotate-0"
        }`}
        onClick={() => setOpenSidbar(!openSidbar)}
      />

      <div className="mb-8 mt-8 flex h-10 w-full items-center justify-center">
        <img src={Image} className={`${!openSidbar && "h-10 w-10 duration-700"} `} alt="logo" />
      </div>

      <ul className="pt-2">{internalMenus.map((menu) => renderMenuItem(menu))}</ul>

      <div
        className={`fixed bottom-2 flex cursor-pointer flex-col items-center justify-center ${
          openSidbar && "w-24 p-2 duration-500"
        } rounded-md`}
      >
        {externalMenus.length > 0 && (
          <ul className="mb-2 w-full border-t border-gray-700 pt-2">
            {externalMenus.map((menu) => renderMenuItem(menu, false))}
          </ul>
        )}

        <li
          onClick={handleLogout}
          className={`flex cursor-pointer items-center justify-center rounded-md p-2 text-sm text-gray-300 hover:bg-light-white ${
            openSidbar ? "w-full" : "w-8"
          }`}
        >
          <span className="flex items-center justify-center text-2xl">
            <LogOut className="size-6 text-white duration-500" />
          </span>
          <span className={`ml-2 text-base font-semibold ${!openSidbar && "hidden"}`}>Sair</span>
        </li>
      </div>
    </div>
  );
}

export { Sidebar };
